import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCredits } from '../../hooks/useCredits';

const LOW_BALANCE_THRESHOLD = 100;

const fmt = (n: number): string => n.toLocaleString();

const CreditsChip: React.FC = () => {
  const { balance, reserved, dailyLimit, isAdmin, loaded, error, refresh } = useCredits();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  if (!loaded && !error) {
    return (
      <span
        className="text-xs text-slate-400 px-2 py-1 rounded border border-white/10 bg-black/10"
        title="Loading credits…"
      >
        ⚡ …
      </span>
    );
  }

  const isLow = balance < LOW_BALANCE_THRESHOLD;
  const tone = isLow
    ? 'text-amber-300 border-amber-500/40 bg-amber-500/10'
    : 'text-white border-white/10 bg-black/20';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); if (!open) refresh(); }}
        title={error ? `Credits unavailable: ${error}` : 'View credits'}
        className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded border transition-colors hover:bg-white/10 ${tone}`}
      >
        <span aria-hidden>⚡</span>
        <span>{error ? '—' : fmt(balance)}</span>
        <span className="text-slate-400 hidden sm:inline">credits</span>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-64 rounded-md border border-white/10 bg-slate-900/95 backdrop-blur p-3 shadow-xl z-50 animate-fade-in">
          <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
            <span>Balance</span>
            <span className="text-white font-medium">{fmt(balance)} ⚡</span>
          </div>
          <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
            <span>Reserved</span>
            <span>{fmt(reserved)}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
            <span>Daily limit</span>
            <span>{dailyLimit > 0 ? fmt(dailyLimit) : '—'}</span>
          </div>
          {isLow && (
            <p className="text-amber-300 text-xs mt-2">
              Low balance — ask an admin to top up.
            </p>
          )}
          {error && (
            <p className="text-red-300 text-xs mt-2">Failed to load: {error}</p>
          )}
          {isAdmin && (
            <Link
              to="/admin"
              onClick={() => setOpen(false)}
              className="mt-3 block w-full text-left text-xs text-sky-300 hover:text-sky-200"
            >
              Open admin credits panel →
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default CreditsChip;
